import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { timerStartRequested, timerStopRequested } from 'features/timer/actions';
import { TimerConfig, TimerControls, TimerData, useTimer } from './useTimer';
import { selectTimerActive, selectTimerEnabled, selectTimerTotalSeconds } from './timerSlice';

export interface TimerContextValue {
  timer: {
    data: TimerData;
    controls: TimerControls;
  } | null;
  isActive: boolean;
  startTimer: (totalSeconds: number) => void;
  stopTimer: () => void;
}

const RoomTimerContext = createContext<TimerContextValue | null>(null);

export const useTimerContext = () => {
  const context = useContext(RoomTimerContext);
  if (!context) {
    throw new Error('useTimerContext must be used within TimerProvider');
  }
  return context;
};

type TimerProviderProps = {
  children: ReactNode;
};

export const TimerProvider = ({ children }: TimerProviderProps) => {
  const dispatch = useAppDispatch();
  const [timerConfig, setTimerConfig] = useState<TimerConfig | null>(null);

  const timerEnabled = useAppSelector(selectTimerEnabled);
  const timerActive = useAppSelector(selectTimerActive);
  const timerTotalSeconds = useAppSelector(selectTimerTotalSeconds);

  const [timerData, timerControls] = useTimer(
    timerConfig || {
      mode: 'countdown',
      totalSeconds: 0,
      autoStart: false,
    }
  );

  useEffect(() => {
    if (timerEnabled && timerActive && timerTotalSeconds > 0) {
      if (!timerConfig || timerConfig.totalSeconds !== timerTotalSeconds) {
        const config: TimerConfig = {
          mode: 'countdown',
          totalSeconds: timerTotalSeconds,
          autoStart: true,
        };
        setTimerConfig(config);
      }
    } else if (!timerEnabled || !timerActive || timerTotalSeconds === 0) {
      if (timerConfig) {
        setTimerConfig(null);
        if (timerData.isRunning || timerData.isPaused) {
          timerControls.stop();
        }
      }
    }
  }, [
    timerEnabled,
    timerActive,
    timerTotalSeconds,
    timerConfig,
    timerControls,
    timerData.isRunning,
    timerData.isPaused,
  ]);

  const startTimer = useCallback(
    (totalSeconds: number) => {
      dispatch(
        timerStartRequested({
          totalSeconds,
        })
      );
    },
    [dispatch]
  );

  const stopTimer = useCallback(() => {
    dispatch(timerStopRequested());
  }, [dispatch]);

  const isActive = timerConfig !== null && timerData.isRunning;

  const timer = useMemo(
    () =>
      isActive
        ? {
            data: timerData,
            controls: timerControls,
          }
        : null,
    [isActive, timerData, timerControls]
  );

  const value = useMemo<TimerContextValue>(
    () => ({
      timer,
      isActive,
      startTimer,
      stopTimer,
    }),
    [timer, isActive, startTimer, stopTimer]
  );

  return <RoomTimerContext.Provider value={value}>{children}</RoomTimerContext.Provider>;
};
